<template>
  <div class="login-page">
    <div class="card">
      <div class="brand">
        <img class="logo" :src="logoIcon" alt="HomeMetric Logo" />
        <h1 class="title">HomeMetric</h1>
      </div>
      <form class="form" @submit.prevent="onLogin">
        <div class="field">
          <label class="label" for="username"><span class="req">*</span> Username</label>
          <div class="input-shell" :class="{ invalid: usernameInvalid }">
            <input
              id="username"
              v-model="username"
              type="text"
              class="input"
              placeholder="Enter username"
              autocomplete="username"
            />
            <img
              v-if="attemptedSubmit && usernameInvalid"
              class="field-error-icon"
              :src="alertRedIcon"
              alt="error"
            />
          </div>
        </div>

        <div class="field">
          <label class="label" for="password"><span class="req">*</span> Password</label>
          <div class="input-shell" :class="{ invalid: passwordInvalid }">
            <input
              id="password"
              v-model="password"
              type="password"
              class="input"
              placeholder="Enter password"
              autocomplete="current-password"
            />
            <img
              v-if="attemptedSubmit && passwordInvalid"
              class="field-error-icon"
              :src="alertRedIcon"
              alt="error"
            />
          </div>
        </div>

        <button type="submit" class="btn-login" :disabled="submitting">Login</button>
      </form>

      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import logoIcon from '@/assets/logo1.png'
import alertRedIcon from '@/assets/alert.red@2x.png'
import { loginApi, type LoginResponse } from '@/api/login'
import { getAdminProfile, type AdminProfile } from '@/api/admin'
import { TOAST_DURATION } from '@/constants/toast'
import ToastComponent from '@/components/ToastComponent.vue'

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const attemptedSubmit = ref(false)
const submitting = ref(false)

const usernameInvalid = ref(false)
const passwordInvalid = ref(false)

const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

function showToast(type: 'success' | 'warning' | 'error', message: string) {
  toast.type = type
  toast.message = message
  toast.visible = true

  setTimeout(() => {
    toast.visible = false
  }, TOAST_DURATION.brief)
}

onMounted(() => {
  if (route.query.passwordChanged === '1') {
    showToast('success', 'Please log in again')
    router.replace({ name: 'Login', query: {} })
  }
})

async function onLogin() {
  attemptedSubmit.value = true

  usernameInvalid.value = !username.value.trim()
  passwordInvalid.value = !password.value

  if (usernameInvalid.value || passwordInvalid.value) {
    showToast('error', 'Please fill in all fields')
    return
  }

  submitting.value = true

  try {
    console.log('开始登录，用户名:', username.value)

    const data: LoginResponse = await loginApi(username.value, password.value)

    console.log('后端返回数据:', data)

    if (data.code === 200 && data.data?.token) {
      localStorage.setItem('token', data.data.token)
      localStorage.setItem('userInfo', JSON.stringify(data.data.user))

      // 等待后端最新资料同步到 localStorage，再跳转 Home
      try {
        const profileRes = await getAdminProfile()
        if (profileRes.code === 200) {
          const p: AdminProfile = profileRes.data
          localStorage.setItem(
            'userInfo',
            JSON.stringify({
              ...data.data.user,
              nickname: p.nickname,
              avatar: p.avatar,
            }),
          )
        }
      } catch {
        // profile-detail 失败不影响跳转
      }

      showToast('success', 'Successfully logged in')
      setTimeout(() => {
        router.push({ name: 'Home' })
      }, 3500)
    } else {
      usernameInvalid.value = true
      passwordInvalid.value = true
      showToast('error', 'Invalid password')
    }
  } catch (error) {
    console.error('Login error:', error)
    usernameInvalid.value = true
    passwordInvalid.value = true
    showToast('error', 'Invalid password')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.login-page {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  font-family:
    system-ui,
    -apple-system,
    'Segoe UI',
    sans-serif;
  position: relative;
}

.card {
  width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.logo {
  width: 128px;
  height: 144px;
  margin-bottom: 8px;
  object-fit: contain;
}

.title {
  width: 194px;
  height: 26px;
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #000;
  letter-spacing: -0.02em;
  text-align: center;
}

.form {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.field .label {
  margin-bottom: 4px;
}

.field:nth-child(1) {
  margin-bottom: 16px;
}

.field:nth-child(2) {
  margin-bottom: 16px;
}

.label {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  text-align: left;
}

.req {
  color: #ff4d4f;
  margin-right: 2px;
}

.input-shell {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #E0E0E0;
  border-radius: 100px;
  background: #ffffff;
  transition: border-color 0.15s ease;
  height: 32px;
  overflow: hidden;
  width: 400px;
}

.input-shell:focus-within:not(.invalid) {
  border-color: #666666;
}

.input-shell.invalid {
  border-color: #e54848;
  background: #fff;
}

.input {
  flex: 1;
  border: none !important;
  outline: none;
  padding: 0 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  background: #ffffff !important;
  width: 100%;
  height: 100%;
  color: #000000;
  border-radius: 0;
}

.input:-webkit-autofill,
.input:-webkit-autofill:hover,
.input:-webkit-autofill:focus,
.input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 100px #ffffff inset !important;
  -webkit-text-fill-color: #000000 !important;
  background-color: #ffffff !important;
  transition: background-color 5000s ease-in-out 0s;
}



.field-error-icon {
  position: absolute;
  right: 10px;
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
  pointer-events: none;
}

.btn-login {
  width: 400px;
  padding: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background: #00aeef;
  border: none;
  border-radius: 100px;
  cursor: pointer;
  transition:
    background 0.15s ease,
    opacity 0.15s ease;
  height: 32px;
  line-height: 32px;
  margin-top: 8px;
}
.btn-login:hover:not(:disabled) {
  background: #4096ff;
}

.btn-login:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
